"use client";

// ── ROW 8 (cards 86bb5yjrg + 86bb5yk8d · scoped approval SG-ROW8-MAP-SCOPED-APPROVAL-20260907) ────
//
// THE TRANSPORT "MAP" PANEL GETS THE REAL MAP. The bus-schedule page's MAP buttons (board header on
// desktop, the MAP pill on mobile) open the design's transport-map panel, whose map area was a
// DECORATIVE PLACEHOLDER: `assets/scenes/background-lowpoly-peaks.png` at 0.55 opacity, zone pins
// positioned by hand-set PERCENTAGES (the design's own comment: "the illustration is schematic, not
// projected"), and +/− buttons wired to `{{ noop }}` — literally dead. Card 86bb5yjrg is the founder
// clicking Map and getting that. Meanwhile the only real basemap on the page mounted into the
// mountain-locator pane at a measured 0×778 — streaming tiles no visitor could see (adjudicated
// 2026-09-07 on 557b0844, refute-first reviewed).
//
// This layer is the bounded repair, in the ledger's own words: "mount the same implementation
// already shipping on the profile route onto the bus-schedule surface, fed by the operational stop
// coordinates; wire the existing MAP / SHOW THE MAP buttons to it." Document-scoped hydrator, same
// family as StopsMediaLayer / CorridorBasemapLayer: it finds the CAPTURED panel and makes it real.
//
//   • The schematic layers are WITHDRAWN, not painted over: a hand-placed pin floating on a real
//     basemap would put a stop where it is not — the invented-location class the approval forbids.
//     The illustration, the percentage pins and the two noop zoom stubs are hidden (restored on
//     cleanup); KeylessMap brings maplibre's own NavigationControl instead of the dead +/−.
//   • MARKERS COME FROM THE OPERATIONAL REGISTRY AND THE PAGE'S OWN CONTENT, nothing else. The
//     drawn set is the intersection registry→page: `/api/transport/geo` records (legacy
//     journey_stop.location; the endpoint labels its own source and the stub tier is REFUSED here,
//     exactly as BookingStopsMap refuses it — founder map ruling 2026-08-27) whose stop name the
//     page's own visible text carries. Coordinates can only come from the registry, names only
//     from the served page — neither side can invent a location. A stop the registry cannot place
//     is simply not drawn; the drawn count and the registry source tier are stated on the host
//     (`data-transport-map-stops` / `data-transport-map-source`) for any gate to read.
//   • The map is opt-in at every viewport. The captured design's MAP control owns its own state;
//     this layer only replaces the panel's placeholder when that control opens it.
//
// Pixel-neutral everywhere else: renders nothing until the panel's own anatomy is found, and a page
// without that anatomy costs one bounded observer.

import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
// finding 8 (cold load) — code-split boundary; see KeylessMapLazy.
import KeylessMap from "@/components/map/KeylessMapLazy";
import type { KeylessMarker } from "@/components/map/KeylessMap";

const norm = (s: string) => s.trim().toLowerCase();

/** Which operational stops belong on THIS page's map: exactly the registry records the page's own
 *  visible content names (the stop-filter rail and the board's DEP/ARR labels both carry them).
 *  The intersection runs registry→page, so a coordinate can only ever come from the registry and a
 *  name can only ever come from the served page — neither side can invent a location. Fare-class
 *  variant records ("… (EXPRESS)") collapse onto the base stop the page names; same physical stop,
 *  same coordinates, drawn once. */
function stopsNamedByPage(
  served: { name?: string; lat?: number; lng?: number }[],
): { name: string; lat: number; lng: number }[] {
  const pageText = norm(document.body.innerText || "");
  const out = new Map<string, { name: string; lat: number; lng: number }>();
  for (const s of served) {
    const { name, lat, lng } = s;
    if (!name || typeof lat !== "number" || typeof lng !== "number" || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const base = name.replace(/\s*\((?:express|executive)\)\s*$/i, "").trim();
    if (!base || !pageText.includes(norm(base))) continue;
    if (!out.has(norm(base))) out.set(norm(base), { name: base, lat, lng });
  }
  return [...out.values()];
}

export default function TransportMapPanelLayer() {
  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | null = null;

    const attach = (illustration: HTMLImageElement): boolean => {
      const area = illustration.parentElement;
      if (!area) return false;
      // The transport panel's map area is the ONE place this scene image shares a box with the
      // "Update list when map moves" chrome — the same image dresses nine other screens.
      if (!/Update list when map moves/i.test(area.textContent || "")) return false;

      // ── 1 · withdraw the invented geography (hidden, not destroyed — cleanup restores it) ──────
      const hidden: { el: HTMLElement; prev: string }[] = [];
      const hide = (el: HTMLElement) => {
        hidden.push({ el, prev: el.style.display });
        el.style.display = "none";
      };
      hide(illustration);
      for (const child of Array.from(area.children) as HTMLElement[]) {
        const st = child.getAttribute("style") ?? "";
        // the percentage pins (translate(-50%, -100%) is their positioning signature)
        if (/translate\(-50%,\s*-100%\)/.test(st)) hide(child);
        // the dead +/− stubs: exactly two buttons reading + and −
        const btns = Array.from(child.querySelectorAll("button"));
        if (
          btns.length === 2 &&
          btns.map((b) => (b.textContent || "").trim()).sort().join("") === "+−"
        )
          hide(child);
      }

      // ── 2 · the real map, on the operational registry ──────────────────────────────────────────
      const host = document.createElement("div");
      host.setAttribute("data-transport-map-host", "");
      // Above the (hidden) illustration, below the panel's own header chrome at z 4.
      host.style.cssText = "position:absolute;inset:0;z-index:1;";
      area.appendChild(host);
      // The panel's own header row (Update-list chrome + ✕) occupies the top strip at z 4, exactly
      // where maplibre parks its NavigationControl — measured: the ✕ covered the zoom-in button's
      // hit target at both viewports. The control drops below the design's chrome instead of
      // fighting it. The style lives on the AREA, not inside `host`: React 18's createRoot(host)
      // clears host's pre-existing children on first render, which silently deleted the first cut
      // of this tag (measured: the offset never applied and the ✕ still swallowed the click).
      const ctrlOffset = document.createElement("style");
      ctrlOffset.setAttribute("data-transport-map-ctrl-offset", "");
      ctrlOffset.textContent = "[data-transport-map-host] .maplibregl-ctrl-top-right{top:52px;}";
      area.appendChild(ctrlOffset);
      const root: Root = createRoot(host);
      // Basemap first — the corridor view with zero markers is the honest state until the
      // registry answers, never a guessed pin (founder map ruling 2026-08-27).
      root.render(<KeylessMap markers={[]} surface="transport-map-panel" />);

      let dead = false;
      interface GeoAnswer {
        source?: string;
        stops?: { name?: string; lat?: number; lng?: number }[];
        mountains?: { code?: string; name?: string; lat?: number; lng?: number }[];
      }
      void (async () => {
        try {
          const geo = (await fetch("/api/transport/geo").then((r) => r.json())) as GeoAnswer;
          if (dead || disposed) return;
          // The stub tier is the ruling's named forbidden source on a visitor surface.
          const served: { name?: string; lat?: number; lng?: number }[] =
            geo.source === "stub" ? [] : Array.isArray(geo.stops) ? geo.stops : [];
          const named = stopsNamedByPage(served);
          // ── QA-BATCH-2 · item 16 (2026-09-17) — THE MAP IS NEVER LEFT BLANK WHEN IT IS SELECTED ──────
          // FWLR-025-R7 · A made the schedule INDEX carry an empty board (results live on the booking URL), so
          // this page names no stop and the intersection above drew ZERO markers: the founder opened MAP and
          // got a bare basemap — an inert map state under a control that promises the route. When the page
          // names nothing, the registry's own operational stops are drawn (real `journey_stop` coordinates,
          // the same tier the results legs use) together with the page's mountain from the same registry.
          // Still never a guess: a coordinate can only come from the registry, and the host states which
          // scope was drawn (`data-transport-map-scope`: "page" or "registry").
          const scope = named.length ? "page" : "registry";
          const drawn = named.length
            ? named
            : served
                .filter((s): s is { name: string; lat: number; lng: number } => !!s.name && Number.isFinite(s.lat) && Number.isFinite(s.lng))
                .map((s) => ({ name: s.name.replace(/\s*\((?:express|executive)\)\s*$/i, "").trim(), lat: s.lat, lng: s.lng }))
                .filter((s, i, all) => all.findIndex((o) => norm(o.name) === norm(s.name)) === i);
          const markers: KeylessMarker[] = drawn.map((s) => ({
            lat: s.lat,
            lng: s.lng,
            name: s.name,
            flag: "stop" as const,
            key: `tmap:${norm(s.name)}`,
          }));
          const code = /^\/mountain-([a-z0-9-]+)/i.exec(window.location.pathname)?.[1]?.toLowerCase();
          const peak = code ? (geo.mountains ?? []).find((m) => String(m.code ?? "").toLowerCase() === code) : undefined;
          if (peak && typeof peak.lat === "number" && typeof peak.lng === "number" && Number.isFinite(peak.lat) && Number.isFinite(peak.lng) && geo.source !== "stub") {
            markers.push({ lat: peak.lat, lng: peak.lng, name: peak.name ?? code, flag: "other" as const, key: `tmap:peak:${code}` });
          }
          host.setAttribute("data-transport-map-stops", String(drawn.length));
          host.setAttribute("data-transport-map-scope", scope);
          host.setAttribute("data-transport-map-source", geo.source ?? "none");
          if (markers.length) root.render(<KeylessMap markers={markers} surface="transport-map-panel" />);
        } catch {
          // The basemap stands; markers simply stay absent. KeylessMap's own failed state covers
          // the provider; this catch covers only the registry fetch.
        }
      })();

      cleanup = () => {
        setTimeout(() => root.unmount(), 0);
        dead = true;
        host.remove();
        ctrlOffset.remove();
        for (const { el, prev } of hidden) el.style.display = prev;
      };
      return true;
    };

    const find = () =>
      Array.from(document.querySelectorAll<HTMLImageElement>('img[src*="background-lowpoly-peaks"]')).find(
        (img) => /Update list when map moves/i.test(img.parentElement?.textContent ?? ""),
      ) ?? null;

    const existing = find();
    if (existing && attach(existing)) return () => cleanup?.();

    // Same wait-for-the-design contract as CorridorBasemapLayer: the export lands client-side after
    // mount, so observe rather than race — bounded, so a page without the panel costs nothing.
    const observer = new MutationObserver(() => {
      if (disposed) return;
      const t = find();
      if (t && attach(t)) {
        observer.disconnect();
        clearTimeout(giveUp);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const giveUp = setTimeout(() => observer.disconnect(), 20000);

    return () => {
      disposed = true;
      clearTimeout(giveUp);
      observer.disconnect();
      cleanup?.();
    };
  }, []);

  return null;
}
